import { useMutation } from '@apollo/client/react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  PortalBusinessDocument,
  PortalMyBusinessesDocument,
  PortalUpdateBusinessDocument,
  type PortalUpdateBusinessMutation,
  type PortalUpdateBusinessMutationVariables,
} from '@/graphql/generated/graphql'

interface UseUpdateBusinessOptions {
  onSuccess?: () => void
}

/**
 * Edición de un negocio (`portalUpdateBusiness`). Refetch del detalle y de la
 * lista para reflejar nombre/logo/direcciones. Toast de error vía onError.
 */
export function useUpdateBusiness(
  businessId: string,
  { onSuccess }: UseUpdateBusinessOptions = {},
) {
  const [mutate, { loading, error }] = useMutation<
    PortalUpdateBusinessMutation,
    PortalUpdateBusinessMutationVariables
  >(PortalUpdateBusinessDocument, {
    refetchQueries: [
      { query: PortalBusinessDocument, variables: { businessId } },
      { query: PortalMyBusinessesDocument },
    ],
    awaitRefetchQueries: true,
    onError: (err) => {
      Sentry.captureException(err, { tags: { operation: 'PortalUpdateBusiness' } })
      toast.error('No se pudo guardar el negocio', { description: err.message })
    },
  })

  const updateBusiness = async (
    input: PortalUpdateBusinessMutationVariables['input'],
  ) => {
    const result = await mutate({ variables: { input } })
    if (result.data?.portalUpdateBusiness && onSuccess) onSuccess()
    return result.data?.portalUpdateBusiness ?? false
  }

  return { updateBusiness, loading, error }
}
